/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from "react";
import type {
  TravelFormData,
  PlanSelection,
  TripCategory,
  TravelInformationSectionProps,
} from "../../types/customer-portal";

import { useProvinces } from "../../hooks/useProvinces";
import { useDestinations } from "../../hooks/useDestinations";
import { useTravelPurpose } from "../../hooks/useTravelPurpose";
import { useGetAllCountries } from "../../hooks/useGetAllCountries";
import { useInternationalDestinations } from "../../hooks/useInternationalDestinations";
import {
  detectSectionFromDestinations,
  getInitialSectionForCategory,
  shouldLoadDestinationsFromSection,
  detectSectionFromTransportMode,
  shouldUseShortSecureLogic,
  shouldResetTravelDatesOnTransportChange,
  shouldResetTravelDatesOnShortSecurePlanChange,
} from "../../utils/sectionDetection";

import SearchableDropdown from "../../../../components/common/SearchableDropdown";
import MultiSelectDropdown from "../../../../components/common/MultiSelectDropdown";
import {
  DatePicker,
  dateUtils,
} from "../../../../shared/components/ui/DatePicker";
import TravelInsuranceReminderModal from "../../../../shared/components/ui/TravelInsuranceReminderModal";
import TripCategoryReminderModal from "../../../../shared/components/ui/TripCategoryReminderModal";
import GroupPlanModal from "../../../../shared/components/ui/GroupPlanModal";
import FamilyPlanModal from "../../../../shared/components/ui/FamilyPlanModal";
import TripCategorySelector from "../TripCategorySelector";
import "./TravelInformationSection.css";
import "../../../../styles/searchable-dropdown.css";
import "../../../../styles/multi-select-dropdown.css";
import { useApplicationFormStore } from "@/store/useApplicationFormStore";
import { parseFormData } from "@/utils/parsers";
import { useAppStore } from "@/store/useAppStore";

import { MdOutlineInfo } from "react-icons/md";
import type { PlanType } from "@/shared/types/applicationTripTypes";

// Field validation highlighting interface
interface FieldValidationErrors {
  [key: string]: boolean;
}

// NOTE: TravelInformationSectionProps is now imported from types file

const TravelInformationSection: React.FC<TravelInformationSectionProps> = ({
  planSelection,
  onShowPlans,
  // NEW: Optional callback for form data changes (Phase 2 - Form Data Capture)
  onFormDataChange,
  // NEW: Optional prefilled data for auto-population (Phase 4 - Auto-population)
  prefilledData,
  // NEW: Optional callback for BUY NOW button functionality
  onBuyNow,
  // NEW: Optional callback for SEE MY QUOTE button to route directly to TravelDetailsSection
  onSeeMyQuote,
}) => {
  // TODO: refactor
  const { setFormData: setTripFormData, setPlanType } =
    useApplicationFormStore();
  const { setAdjustmentPriceByAge, setPaymentConvertedPrice } = useAppStore();

  // Helper function to convert landing page planType to TravelFormData planType
  const convertPlanType = (
    landingPlanType: PlanSelection["planType"],
  ): TravelFormData["planType"] => {
    switch (landingPlanType) {
      case "Individual Plan":
        return "Individual";
      case "Family Plan":
        return "Family";
      case "Group Plan":
        return "Group";
      case "Multi Trip":
        return "Multi Trip";
      default:
        return "";
    }
  };

  // Helper function to get initial trip category from plan selection
  const getInitialTripCategory = (): TripCategory => {
    if (!planSelection?.section) return "International";

    switch (planSelection.section) {
      case "WorldwideElite":
      case "Asia and Oceania":
        return "International";
      case "Domestic":
      case "ShortSecure":
        return "Domestic";
      default:
        return "International";
    }
  };

  // Trip Category state management - consider prefilled data
  const [tripCategory, setTripCategory] = useState<TripCategory>(() => {
    // If prefilled data has trip category, use it; otherwise use initial logic
    return prefilledData?.tripCategory !== undefined
      ? prefilledData.tripCategory
      : getInitialTripCategory();
  });
  const [detectedSection, setDetectedSection] = useState<
    PlanSelection["section"] | undefined
  >(planSelection?.section);

  // Trip Category should always be shown in the travel information section
  const shouldShowTripCategory = true;

  // NEW: Initialize form data with prefilled data if available (Phase 4 - Auto-population)
  const getInitialFormData = (): TravelFormData => {
    const baseData: TravelFormData = {
      tripType: planSelection?.planType === 'Multi Trip' ? 'Annual Multi-Trip' : 'Single Trip',
      planType: planSelection?.planType === 'Multi Trip' ? 'Multi Trip' : convertPlanType(planSelection?.planType || 'Individual Plan'),
      startTrip: '',
      destination: [],
      endTrip: '',
      purposeOfTravel: '',
      startDate: '',
      endDate: '',
      travelDate: '',
      philippinesEndpoint: undefined,
      transportMode: undefined,
      tripCategory: shouldShowTripCategory ? getInitialTripCategory() : undefined,
      homeCountry: ''
    };

    // If prefilled data is available, merge it with base data (Phase 4 - Auto-population)
    if (prefilledData) {
      const mergedData = {
        ...baseData,
        // Only use prefilled values if they're not empty/undefined - Fixed logic
        tripType:
          prefilledData.tripType && prefilledData.tripType.trim() !== ""
            ? prefilledData.tripType
            : baseData.tripType,
        planType: prefilledData.planType,
        startTrip:
          prefilledData.startTrip && prefilledData.startTrip.trim() !== ""
            ? prefilledData.startTrip
            : baseData.startTrip,
        destination:
          prefilledData.destination &&
          Array.isArray(prefilledData.destination) &&
          prefilledData.destination.length > 0
            ? prefilledData.destination
            : baseData.destination,
        endTrip:
          prefilledData.endTrip && prefilledData.endTrip.trim() !== ""
            ? prefilledData.endTrip
            : baseData.endTrip,
        purposeOfTravel:
          prefilledData.purposeOfTravel &&
          prefilledData.purposeOfTravel.trim() !== ""
            ? prefilledData.purposeOfTravel
            : baseData.purposeOfTravel,
        startDate:
          prefilledData.startDate && prefilledData.startDate.trim() !== ""
            ? prefilledData.startDate
            : baseData.startDate,
        endDate:
          prefilledData.endDate && prefilledData.endDate.trim() !== ""
            ? prefilledData.endDate
            : baseData.endDate,
        travelDate:
          prefilledData.travelDate && prefilledData.travelDate.trim() !== ""
            ? prefilledData.travelDate
            : baseData.travelDate,
        philippinesEndpoint:
          prefilledData.philippinesEndpoint !== undefined
            ? prefilledData.philippinesEndpoint
            : baseData.philippinesEndpoint,
        transportMode:
          prefilledData.transportMode !== undefined
            ? prefilledData.transportMode
            : baseData.transportMode,
        tripCategory:
          prefilledData.tripCategory !== undefined
            ? prefilledData.tripCategory
            : baseData.tripCategory,
        homeCountry:
          prefilledData.homeCountry && prefilledData.homeCountry.trim() !== ""
            ? prefilledData.homeCountry
            : baseData.homeCountry,
      };
      return mergedData;
    }

    return baseData;
  };

  const [formData, setFormData] =
    useState<TravelFormData>(getInitialFormData());

  // Get effective section (from planSelection or detected from trip category)
  // FIXED: Properly handle trip category changes to update section
  const effectiveSection = useMemo(() => {
    // CRITICAL FIX: When trip category is manually changed, prioritize the new category over original planSelection
    // This ensures destination options update correctly when switching categories
    if (shouldShowTripCategory) {
      // For Domestic category, derive the section directly from the selected transport mode
      // (Via Land or Sea -> ShortSecure, Via Air -> Domestic) instead of the detectedSection
      // state, which can fall out of sync and keep returning "Domestic" for ShortSecure trips
      if (tripCategory === "Domestic") {
        if (
          formData.transportMode === "land" ||
          formData.transportMode === "sea"
        ) {
          return "ShortSecure";
        }
        return "Domestic"; // Via Air, or no transport mode selected yet
      }

      // For International category, use detected section if available, otherwise default to undefined
      // This allows proper section detection based on destination selection
      if (tripCategory === "International") {
        return detectedSection; // This will be undefined initially, then set by destination selection
      }

      // For Inbound category, use Domestic section logic
      if (tripCategory === "Inbound") {
        return detectedSection;
      }
    }

    // Fallback to original planSelection section if no trip category is set
    return planSelection?.section || detectedSection;
  }, [
    shouldShowTripCategory,
    tripCategory,
    formData.transportMode,
    detectedSection,
    planSelection?.section,
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
  const [isTripCategoryInfoModalOpen, setIsTripCategoryInfoModalOpen] =
    useState(false);

  // Field validation highlighting state
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Use the custom hook to fetch provinces
  const { startAndEndlocationOptions, loading: provincesLoading } =
    useProvinces();

  // Use the custom hook to fetch destinations based on effective section
  const { destinationLocationOptions, loading: DestinationLoading } =
    useDestinations(
      shouldLoadDestinationsFromSection(tripCategory, effectiveSection)
        ? effectiveSection
        : undefined,
    );

  // Use the International destinations hook for International category without pre-selected section
  const {
    destinations: internationalDestinations,
    destinationLocationOptions: internationalDestinationOptions,
    loading: internationalLoading,
  } = useInternationalDestinations();

  // Use the custom hook to fetch travel purposes
  const { tripPurposeOptions } = useTravelPurpose();

  // "Others" reveals a "Please specify" field and should always be the last item in the list
  const purposeOfTravelOptions = useMemo(
    () =>
      tripPurposeOptions.includes("Others")
        ? [
            ...tripPurposeOptions.filter((option) => option !== "Others"),
            "Others",
          ]
        : tripPurposeOptions,
    [tripPurposeOptions],
  );

  // "Business" gets an expanded label in the dropdown to clarify what counts as business travel
  const getPurposeOfTravelLabel = (option: string) =>
    option === "Business"
      ? "Business (i.e., attending conference, meeting, etc.)"
      : option;

  // Use the custom hook to fetch countries (for inbound category)
  const { inboundlocationOptions, loading: inboundLoading } =
    useGetAllCountries();

  // Handle trip category change
  const handleTripCategoryChange = (category: TripCategory) => {
    setTripCategory(category);

    // Reset all form fields to their default state when category changes
    setFormData(() => ({
      tripType: "Single Trip",
      planType: "",
      startTrip: "",
      destination: category === "Inbound" ? ["Philippines"] : [],
      endTrip: "",
      purposeOfTravel: "",
      startDate: "",
      endDate: "",
      travelDate: "",
      philippinesEndpoint: undefined,
      transportMode: undefined,
      tripCategory: category,
      homeCountry: "",
    }));

    // Clear any field validation errors carried over from the previous category
    setFieldErrors({});
    setShowValidationErrors(false);

    // Always reset detected section when category changes to ensure proper state cleanup
    // This prevents stale international section values from affecting domestic category logic
    const initialSection = getInitialSectionForCategory(category);
    setDetectedSection(initialSection);

    // For International category without pre-selected section, clear detected section
    // to allow dynamic detection based on destination selection
    if (category === "International" && !planSelection?.section) {
      setDetectedSection(undefined);
    }
  };

  // Enhanced destination change handler with auto-detection
  const handleDestinationChange = (destinationNames: string[]) => {
    handleInputChange("destination", destinationNames);

    // Auto-detect section for International category
    if (
      shouldShowTripCategory &&
      tripCategory === "International" &&
      destinationNames.length > 0
    ) {
      // For International category, ALWAYS use International destinations for detection
      // This ensures consistent section detection regardless of pre-selected sections
      const destinationsToSearch = internationalDestinations;

      // Find destination objects to check areaType
      const selectedDestinations = destinationsToSearch.filter((dest) =>
        destinationNames.includes(dest.destinationName),
      );

      if (selectedDestinations.length > 0) {
        const newSection = detectSectionFromDestinations(
          selectedDestinations,
          tripCategory,
        );
        setDetectedSection(newSection);
      }
    }
  };

  const handleInputChange = (
    field: keyof TravelFormData,
    value: string | string[],
  ) => {
    // Clear field error when user starts typing
    clearFieldError(field);

    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // NEW: Notify parent component of form data changes (Phase 2 - Form Data Capture)
      if (onFormDataChange) {
        // Use setTimeout to ensure state update is complete before calling callback
        setTimeout(() => {
          onFormDataChange(newData);
        }, 0);
      }

      // If transport mode changes for Domestic category, update detected section
      if (
        field === "transportMode" &&
        typeof value === "string" &&
        shouldShowTripCategory &&
        tripCategory === "Domestic"
      ) {
        const previousTransportMode = prev.transportMode;
        const newTransportMode = value as "land" | "sea" | "air";

        const newSection = detectSectionFromTransportMode(newTransportMode);
        setDetectedSection(newSection);

        // Clear plan type to force reselection with new restrictions
        newData.planType = "";

        // ENHANCED: Reset travel dates when switching between different sections
        if (
          shouldResetTravelDatesOnTransportChange(
            previousTransportMode,
            newTransportMode,
          )
        ) {
          newData.startDate = "";
          newData.endDate = "";
        }

        // CRITICAL FIX: When switching from Via Air to Via Land/Sea, reset to Single Trip
        // Via Land and Via Sea only support Single Trip, not Multi Trip
        if (newTransportMode === "land" || newTransportMode === "sea") {
          // Force trip type to Single Trip for land/sea transport modes
          newData.tripType = "Single Trip";

          // Clear Multi Trip specific fields if they were previously set
          if (prev.tripType === "Annual Multi-Trip") {
            newData.travelDate = "";
            // Reset other fields that might have been cleared for Multi Trip
            // but should be available for Single Trip
          }
        }
      }

      // If trip type changes, clear fields that are not relevant to the new trip type
      if (field === "tripType" && typeof value === "string") {
        if (value === "Annual Multi-Trip") {
          // Clear Single Trip specific fields and set planType to Multi Trip
          newData.planType = "Multi Trip";
          newData.startTrip = "";
          newData.destination = [];
          newData.endTrip = "";
          newData.startDate = "";
          newData.endDate = "";
        } else if (value === "Single Trip") {
          // Clear Annual Multi-Trip specific fields and reset planType based on planSelection
          newData.planType = newData.tripType === 'Annual Multi-Trip' ? "" : newData.planType;
          newData.travelDate = "";
        }
      }

      // If Philippines endpoint selection changes, clear endTrip to force reselection
      if (field === "philippinesEndpoint") {
        newData.endTrip = "";
      }

      // If start date changes, clear end date if it's invalid
      if (field === "startDate" && typeof value === "string") {
        const startDate = dateUtils.parseDate(value);
        const endDate = dateUtils.parseDate(prev.endDate);

        if (startDate && endDate) {
          let maxEndDate: Date;

          // Use Short Secure specific limits for Short Secure plans
          if (
            isShortSecure() &&
            prev.planType &&
            (prev.planType === "Individual" || prev.planType === "Group")
          ) {
            const maxDays = prev.planType === "Group" ? 5 : 15;
            maxEndDate = dateUtils.addDays(startDate, maxDays);
          } else {
            // Use standard limits for other plans
            maxEndDate = dateUtils.addDays(
              startDate,
              prev.tripType === "Single Trip" ? 180 : 365,
            );
          }

          if (endDate > maxEndDate || endDate < startDate) {
            newData.endDate = "";
          }
        }
      }

      // ENHANCED: Reset travel dates when switching between Individual and Group in ShortSecure
      if (field === "planType" && typeof value === "string") {
        const previousPlanType = prev.planType as "Individual" | "Group" | "";
        const newPlanType = value as "Individual" | "Group" | "";
        const isCurrentlyShortSecure = isShortSecure();

        if (
          shouldResetTravelDatesOnShortSecurePlanChange(
            previousPlanType,
            newPlanType,
            isCurrentlyShortSecure,
          )
        ) {
          newData.startDate = "";
          newData.endDate = "";
        }
      }

      // If plan type is "Group", show the Group modal
      if (
        field === "planType" &&
        typeof value === "string" &&
        value === "Group"
      ) {
        setIsGroupModalOpen(true);
      }

      // If plan type is "Family", show the Family modal
      if (
        field === "planType" &&
        typeof value === "string" &&
        value === "Family"
      ) {
        setIsFamilyModalOpen(true);
      }

      return newData;
    });
  };

  // Effect to initialize trip category state when component mounts
  useEffect(() => {
    if (shouldShowTripCategory && !planSelection?.section) {
      // Always set initial detected section based on category for consistency
      const initialSection = getInitialSectionForCategory(tripCategory);
      setDetectedSection(initialSection);

      // For International category, clear detected section to allow dynamic detection
      if (tripCategory === "International") {
        setDetectedSection(undefined);
      }

      if (tripCategory === "Inbound") {
        setDetectedSection(undefined);
        handleDestinationChange(["Philippines"]);
      }
    }
  }, [shouldShowTripCategory, planSelection?.section, tripCategory]);

  const handleShowPlans = () => {
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;

    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }

    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);


    // For ShortSecure, ensure we have the transport mode
    if (isShortSecure() && formData.transportMode) {
      console.log("ShortSecure transport mode:", formData.transportMode);
    }

    if (onShowPlans) {
      // CRITICAL FIX: Properly map trip category to section for multi-trip plans
      let mappedSection = effectiveSection;

      // SPECIAL CASE: For International Multi Trip, always use undefined to fetch both international sections
      if (
        shouldShowTripCategory &&
        tripCategory === "International" &&
        formData.tripType === "Annual Multi-Trip"
      ) {
        mappedSection = undefined;
      }
      // ADDITIONAL CHECK: Also check if planSelection indicates Multi Trip with International section
      else if (
        planSelection?.planType === "Multi Trip" &&
        (planSelection?.section === "Asia and Oceania" ||
          planSelection?.section === "WorldwideElite")
      ) {
        mappedSection = undefined;
      }
      // If no effective section detected, map trip category to default section
      else if (!mappedSection) {
        if (shouldShowTripCategory) {
          switch (tripCategory) {
            case "Domestic":
              // Via Land or Sea -> ShortSecure (ds_name=short_secure), Via Air -> Domestic (ds_name=domestic)
              mappedSection =
                formData.transportMode === "land" ||
                formData.transportMode === "sea"
                  ? "ShortSecure"
                  : "Domestic";
              break;
            case "International":
              // For International without destination selection, default to undefined to fetch both sections
              mappedSection = undefined;
              break;
            case "Inbound":
              mappedSection = "Domestic"; // Inbound uses domestic logic
              break;
            default:
              mappedSection = "Domestic";
          }
        }
      }

      // Create updated plan selection with properly mapped section
      const updatedPlanSelection: PlanSelection | undefined = planSelection
        ? {
            ...planSelection,
            section:
              mappedSection !== undefined
                ? mappedSection
                : planSelection.section,
          }
        : mappedSection !== undefined
          ? {
              planType: "Individual Plan", // Default plan type for trip category flow
              section: mappedSection,
            }
          : undefined;

      // TODO: refactor
      setTripFormData(parseFormData(formData));
      setAdjustmentPriceByAge(0);
      setPaymentConvertedPrice(undefined);

      // Pass both formData and updated planSelection to preserve section info
      onShowPlans(formData, updatedPlanSelection);
    }
  };

  // NEW: Handler for SEE MY QUOTE button - routes directly to TravelDetailsSection
  const handleSeeMyQuote = () => {
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;

    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }

    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);

    // For ShortSecure, ensure we have the transport mode
    if (isShortSecure() && formData.transportMode) {
      console.log("ShortSecure transport mode:", formData.transportMode);
    }

    if (onSeeMyQuote) {
      // CRITICAL FIX: Use the same section mapping logic as handleShowPlans for consistency
      let mappedSection = effectiveSection;

      // SPECIAL CASE: For International Multi Trip, always use undefined to fetch both international sections
      if (
        shouldShowTripCategory &&
        tripCategory === "International" &&
        formData.tripType === "Annual Multi-Trip"
      ) {
        mappedSection = undefined;
      }
      // ADDITIONAL CHECK: Also check if planSelection indicates Multi Trip with International section
      else if (
        planSelection?.planType === "Multi Trip" &&
        (planSelection?.section === "Asia and Oceania" ||
          planSelection?.section === "WorldwideElite")
      ) {
        mappedSection = undefined;
      }
      // If no effective section detected, map trip category to default section
      else if (!mappedSection) {
        if (shouldShowTripCategory) {
          switch (tripCategory) {
            case "Domestic":
              // Via Land or Sea -> ShortSecure (ds_name=short_secure), Via Air -> Domestic (ds_name=domestic)
              mappedSection =
                formData.transportMode === "land" ||
                formData.transportMode === "sea"
                  ? "ShortSecure"
                  : "Domestic";
              break;
            case "International":
              // For International without destination selection, default to undefined to fetch both sections
              mappedSection = undefined;
              break;
            case "Inbound":
              mappedSection = "Domestic"; // Inbound uses domestic logic
              break;
            default:
              mappedSection = "Domestic";
          }
        }
      }
      // Create updated plan selection with properly mapped section
      const updatedPlanSelection: PlanSelection | undefined = planSelection
        ? {
            ...planSelection,
            section:
              mappedSection !== undefined
                ? mappedSection
                : planSelection.section,
          }
        : mappedSection !== undefined
          ? {
              planType: "Individual Plan", // Default plan type for trip category flow
              section: mappedSection,
            }
          : undefined;

      // Pass both formData and updated planSelection to preserve section info

      // TODO: refactor
      setTripFormData(parseFormData(formData));

      onSeeMyQuote(formData, updatedPlanSelection);
    }
  };

  // NEW: Handler for BUY NOW button
  const handleBuyNow = () => {
    // For Domestic category with transport mode, determine the effective section

    // For ShortSecure, ensure we have the transport mode
    if (isShortSecure() && formData.transportMode) {
      console.log("ShortSecure transport mode:", formData.transportMode);
    }

    if (onBuyNow) {
      // CRITICAL FIX: Use the same section mapping logic as handleShowPlans for consistency
      let mappedSection = effectiveSection;

      // SPECIAL CASE: For International Multi Trip, always use undefined to fetch both international sections
      if (
        shouldShowTripCategory &&
        tripCategory === "International" &&
        formData.tripType === "Annual Multi-Trip"
      ) {
        mappedSection = undefined;
      }
      // ADDITIONAL CHECK: Also check if planSelection indicates Multi Trip with International section
      else if (
        planSelection?.planType === "Multi Trip" &&
        (planSelection?.section === "Asia and Oceania" ||
          planSelection?.section === "WorldwideElite")
      ) {
        mappedSection = undefined;
      }
      // If no effective section detected, map trip category to default section
      else if (!mappedSection) {
        if (shouldShowTripCategory) {
          switch (tripCategory) {
            case "Domestic":
              // Via Land or Sea -> ShortSecure (ds_name=short_secure), Via Air -> Domestic (ds_name=domestic)
              mappedSection =
                formData.transportMode === "land" ||
                formData.transportMode === "sea"
                  ? "ShortSecure"
                  : "Domestic";
              break;
            case "International":
              // For International without destination selection, default to undefined to fetch both sections
              mappedSection = undefined;
              break;
            case "Inbound":
              mappedSection = "Domestic"; // Inbound uses domestic logic
              break;
            default:
              mappedSection = "Domestic";
          }
        }
      }

      // Create updated plan selection with properly mapped section
      const updatedPlanSelection: PlanSelection | undefined = planSelection
        ? {
            ...planSelection,
            section:
              mappedSection !== undefined
                ? mappedSection
                : planSelection.section,
          }
        : mappedSection !== undefined
          ? {
              planType: "Individual Plan", // Default plan type for trip category flow
              section: mappedSection,
            }
          : undefined;

      // TODO: refactor
      setTripFormData(parseFormData(formData));

      // Pass both formData and updated planSelection to preserve section info
      onBuyNow(formData, updatedPlanSelection);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
  };

  const handleCloseFamilyModal = () => {
    setIsFamilyModalOpen(false);
  };

  const handleTripCategoryInfoIconClick = () => {
    setIsTripCategoryInfoModalOpen(true);
  };

  const handleCloseTripCategoryInfoModal = () => {
    setIsTripCategoryInfoModalOpen(false);
  };

  // Helper function to determine if Philippines question should be shown
  const shouldShowPhilippinesQuestion = () => {
    // Show for International category (which covers both WorldwideElite and Asia and Oceania)
    // or for pre-selected WorldwideElite/Asia and Oceania sections

    if (tripCategory === "Inbound") {
      return false;
    }

    const isInternationalCategory =
      shouldShowTripCategory && tripCategory === "International";

    // Only consider planSelection?.section if the current trip category is International
    // This prevents stale international section values from affecting domestic category logic
    const isInternationalSection =
      isInternationalCategory &&
      (planSelection?.section === "WorldwideElite" ||
        planSelection?.section === "Asia and Oceania");

    const isDetectedInternationalSection =
      detectedSection === "WorldwideElite" ||
      detectedSection === "Asia and Oceania";

    return (
      (isInternationalCategory ||
        isInternationalSection ||
        isDetectedInternationalSection) &&
      formData.tripType === "Single Trip"
    );
  };

  // Helper function to determine if this is ShortSecure section
  const isShortSecure = () => {
    // Check if we're using ShortSecure logic based on Domestic category + transport mode
    if (
      shouldShowTripCategory &&
      tripCategory === "Domestic" &&
      formData.transportMode
    ) {
      return shouldUseShortSecureLogic(tripCategory, formData.transportMode);
    }
    // Fallback to original logic for direct ShortSecure section selection
    return planSelection?.section === "ShortSecure";
  };

  // Helper function to determine if transport mode should be shown
  const shouldShowTransportMode = () => {
    // Show transport mode selection for Domestic trip category
    return shouldShowTripCategory && tripCategory === "Domestic";
  };

  // Helper function to determine if Type of Trip should be shown for Domestic category with Via Air
  const shouldShowTripTypeForDomesticAir = () => {
    // Show Type of Trip section ONLY for Domestic category when Via Air is selected
    return (
      shouldShowTripCategory &&
      tripCategory === "Domestic" &&
      formData.transportMode === "air"
    );
  };

  // Helper function to determine if Type of Trip section should be shown
  const shouldShowTypeOfTrip = () => {
    // Hide Type of Trip section for Inbound category (always Single Trip by default)
    if (shouldShowTripCategory && tripCategory === "Inbound") {
      return false;
    }
    // For other categories, use existing logic
    return !shouldShowTransportMode() && !isShortSecure();
  };

  // Helper function to get destination options based on trip category
  const getDestinationOptions = () => {
    if (shouldShowTripCategory && tripCategory === "Inbound") {
      return inboundlocationOptions;
    }

    // For International category, ALWAYS use International destinations for consistency
    // This ensures data consistency between landing and travel information sections
    if (shouldShowTripCategory && tripCategory === "International") {
      return internationalDestinationOptions;
    }

    return destinationLocationOptions;
  };

  // Helper function to get destination loading state
  const getDestinationLoading = () => {
    if (shouldShowTripCategory && tripCategory === "Inbound") {
      return inboundLoading;
    }

    // For International category, ALWAYS use International loading for consistency
    // This ensures loading state consistency between landing and travel information sections
    if (shouldShowTripCategory && tripCategory === "International") {
      return internationalLoading;
    }

    return DestinationLoading;
  };

  // Helper function to determine loading state for End Trip dropdown
  const getEndTripLoading = () => {
    if (
      shouldShowPhilippinesQuestion() &&
      formData.philippinesEndpoint === "No"
    ) {
      return inboundLoading;
    }
    return provincesLoading;
  };

  // Helper function to determine placeholder text for End Trip dropdown
  const getEndTripPlaceholder = () => {
    if (tripCategory === ("Inbound" as TripCategory)) {
      return inboundLoading ? "Loading countries..." : "Return country";
    }
    if (
      shouldShowPhilippinesQuestion() &&
      formData.philippinesEndpoint === "No"
    ) {
      return inboundLoading ? "Loading countries..." : "Choose your end trip";
    }
    return provincesLoading ? "Loading provinces..." : "Choose your end trip";
  };

  // Get validation errors for required fields
  const getValidationErrors = (): FieldValidationErrors => {
    const errors: FieldValidationErrors = {};

    // For Inbound category, validate homeCountry and Single Trip fields
    if (shouldShowTripCategory && tripCategory === "Inbound") {
      // if (!formData.homeCountry) errors.homeCountry = true;
      if (!formData.planType) errors.planType = true;
      if (!formData.startTrip) errors.startTrip = true;
      if (formData.destination.length === 0) errors.destination = true;
      // if (!formData.endTrip) errors.endTrip = true;
      if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
      if (!formData.startDate) errors.startDate = true;
      if (!formData.endDate) errors.endDate = true;
      return errors;
    }

    // For Domestic category with transport mode selection
    if (shouldShowTransportMode()) {
      // Base validation for Domestic category
      if (!formData.transportMode) errors.transportMode = true;

      // If Type of Trip is shown for Domestic Air, validate trip type
      if (shouldShowTripTypeForDomesticAir()) {
        // For Domestic Air with Type of Trip selection
        if (formData.tripType === "Annual Multi-Trip") {
          // Annual Multi-Trip validation for Domestic Air
          if (!formData.tripType) errors.tripType = true;
          if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
          if (!formData.travelDate) errors.travelDate = true;
        } else {
          // Single Trip validation for Domestic Air
          if (!formData.tripType) errors.tripType = true;
          if (!formData.planType) errors.planType = true;
          if (!formData.startTrip) errors.startTrip = true;
          if (formData.destination.length === 0) errors.destination = true;
          if (!formData.endTrip) errors.endTrip = true;
          if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
          if (!formData.startDate) errors.startDate = true;
          if (!formData.endDate) errors.endDate = true;
        }
      } else {
        // For Domestic Land/Sea (ShortSecure logic) - no trip type selection
        if (!formData.planType) errors.planType = true;
        if (!formData.startTrip) errors.startTrip = true;
        if (formData.destination.length === 0) errors.destination = true;
        if (!formData.endTrip) errors.endTrip = true;
        if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
        if (!formData.startDate) errors.startDate = true;
        if (!formData.endDate) errors.endDate = true;
      }
    } else if (isShortSecure()) {
      // For direct ShortSecure section (legacy), validate transport mode and other Single Trip fields
      if (!formData.transportMode) errors.transportMode = true;
      if (!formData.planType) errors.planType = true;
      if (!formData.startTrip) errors.startTrip = true;
      if (formData.destination.length === 0) errors.destination = true;
      if (!formData.endTrip) errors.endTrip = true;
      if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
      if (!formData.startDate) errors.startDate = true;
      if (!formData.endDate) errors.endDate = true;
    } else if (formData.tripType === "Annual Multi-Trip") {
      // For Annual Multi-Trip, validate purpose of travel and travel date
      if (!formData.tripType) errors.tripType = true;
      if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
      if (!formData.travelDate) errors.travelDate = true;
    } else {
      // For Single Trip, validate all fields as before
      if (!formData.tripType) errors.tripType = true;
      if (!formData.planType) errors.planType = true;
      if (!formData.startTrip) errors.startTrip = true;
      if (formData.destination.length === 0) errors.destination = true;
      if (!formData.endTrip) errors.endTrip = true;
      if (!formData.purposeOfTravel) errors.purposeOfTravel = true;
      if (!formData.startDate) errors.startDate = true;
      if (!formData.endDate) errors.endDate = true;
    }

    // If Philippines question should be shown (only for Single Trip + WorldwideElite/Asia and Oceania), also validate that field
    if (shouldShowPhilippinesQuestion()) {
      if (formData.philippinesEndpoint === undefined)
        errors.philippinesEndpoint = true;
    }

    return errors;
  };

  // Plan type options - limited for ShortSecure (land/sea transport modes)
  const planTypeOptions: TravelFormData["planType"][] = isShortSecure()
    ? //Removed group for now(12/10/2025)
      ["Individual"] // ShortSecure (land/sea) only supports Individual and Group
    : ["Individual", "Family"]; // Domestic (air) and other sections support all plan types

  // Create validation rules for start and end dates
  const startDateRules = useMemo(() => {
    return dateUtils.createStartDateRules();
  }, []);

  const endDateRules = useMemo(() => {
    const startDate = dateUtils.parseDate(formData.startDate);
    if (startDate) {
      // Use Short Secure specific date validation for Short Secure plans
      if (
        isShortSecure() &&
        formData.planType &&
        (formData.planType === "Individual" || formData.planType === "Group")
      ) {
        return dateUtils.createShortSecureEndDateRules(
          startDate,
          formData.planType,
        );
      }
      // Use standard date validation for all other plans
      return dateUtils.createEndDateRules(startDate, formData.tripType);
    }
    return dateUtils.createStartDateRules();
  }, [formData.startDate, formData.tripType, formData.planType, isShortSecure]);

  // Create validation rules for Annual Multi-Trip travel date
  const travelDateRules = useMemo(() => {
    return dateUtils.createStartDateRules();
  }, []);

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? "field-error" : "";
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  return (
    <div className="travel-information-container">
      <div className="travel-information-background" />

      <div
        className={`travel-information-form-container ${formData.tripType === "Annual Multi-Trip" ? "annual-multi-trip" : ""}`}
      >
        {/* NEW: Travel Information Header */}
        <div className="travel-information-form-header">
          <h2>Your Travel Information:</h2>
        </div>

        {/* NEW: Trip Category Selector (conditional) */}
        <div className="trip-category-label-with-icon">
          <label className="form-label">
            Trip Category
            <span
              className="info-icon clickable trip-category-info-icon"
              title="Click for trip category information"
              onClick={handleTripCategoryInfoIconClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTripCategoryInfoIconClick();
                }
              }}
              aria-label="Trip category information"
            >
              <MdOutlineInfo />
            </span>
          </label>
        </div>
        {shouldShowTripCategory && (
          <TripCategorySelector
            selectedCategory={tripCategory}
            onCategoryChange={handleTripCategoryChange}
            transportMode={formData.transportMode}
          />
        )}

        <form className="travel-information-form">
          {/* Transport Mode Selection for Domestic Category */}
          {shouldShowTransportMode() ? (
            <>
              <div className="travel-plan-purpose-form-row">
                {/* Mode of Travel */}
                <div
                  className={`travel-information-form-group ${getFieldErrorClass("transportMode")}`}
                >
                  <label className="form-label">
                    Select your mode of travel *
                  </label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="transportMode"
                        value="land"
                        checked={formData.transportMode === "land"}
                        onChange={(e) =>
                          handleInputChange(
                            "transportMode",
                            e.target.value as "land" | "sea" | "air",
                          )
                        }
                      />
                      <span className="radio-custom"></span>
                      Via Land or Sea
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="transportMode"
                        value="air"
                        checked={formData.transportMode === "air"}
                        onChange={(e) =>
                          handleInputChange(
                            "transportMode",
                            e.target.value as "land" | "sea" | "air",
                          )
                        }
                      />
                      <span className="radio-custom"></span>
                      Via Air
                    </label>
                  </div>
                </div>

                {/* Type of Trip for Domestic Category - Only show when Via Air is selected */}
                {shouldShowTripTypeForDomesticAir() && (
                  <div
                    className={`travel-information-form-group ${getFieldErrorClass("tripType")}`}
                  >
                    <label className="form-label">Type of Trip</label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="tripType"
                          value="Single Trip"
                          checked={formData.tripType === "Single Trip"}
                          onChange={(e) =>
                            handleInputChange("tripType", e.target.value)
                          }
                        />
                        <span className="radio-custom"></span>
                        Single Trip
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="tripType"
                          value="Annual Multi-Trip"
                          checked={formData.tripType === "Annual Multi-Trip"}
                          onChange={(e) =>
                            handleInputChange("tripType", e.target.value)
                          }
                        />
                        <span className="radio-custom"></span>
                        Multi Trip
                        <span
                          className="info-icon multi-trip-tooltip clickable"
                          title="Multi Trip information"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                            }
                          }}
                          aria-label="Multi Trip information"
                        >
                          <MdOutlineInfo />
                          <span className="tooltip-content">
                            <span className="tooltip-title">Multi Trip</span>
                            Ideal for the frequent traveler -- travel
                            insurance for unlimited number of trips per year,
                            90 days per trip.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Plan Type - own row for Domestic Single Trip */}
              {formData.tripType === "Single Trip" && (
                <div className="travel-plan-purpose-form-row">
                  <div
                    className={`travel-information-form-group ${getFieldErrorClass("planType")}`}
                  >
                    <label className="form-label">Plan *</label>
                    <div className="radio-group">
                      {planTypeOptions.map((option) => (
                        <label className="radio-option" key={option}>
                          <input
                            type="radio"
                            name="planType"
                            value={option}
                            checked={formData.planType === option}
                            onChange={(e) => {
                              setPlanType(e.target.value as PlanType);
                              handleInputChange(
                                "planType",
                                e.target.value as TravelFormData["planType"],
                              );
                            }}
                          />
                          <span className="radio-custom"></span>
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : shouldShowTypeOfTrip() ? (
            /* Non-Domestic categories: Show trip type selection (except Inbound) */
            <div className="travel-plan-purpose-form-row">
              <div
                className={`travel-information-form-group ${getFieldErrorClass("tripType")}`}
              >
                <label className="form-label">Choose type of trip *</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="tripType"
                      value="Single Trip"
                      checked={formData.tripType === "Single Trip"}
                      onChange={(e) =>
                        handleInputChange("tripType", e.target.value)
                      }
                    />
                    <span className="radio-custom"></span>
                    Single Trip
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="tripType"
                      value="Annual Multi-Trip"
                      checked={formData.tripType === "Annual Multi-Trip"}
                      onChange={(e) =>
                        handleInputChange("tripType", e.target.value)
                      }
                    />
                    <span className="radio-custom"></span>
                    Multi Trip
                    <span
                      className="info-icon multi-trip-tooltip clickable"
                      title="Multi Trip information"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                        }
                      }}
                      aria-label="Multi Trip information"
                    >
                      <MdOutlineInfo />
                      <span className="tooltip-content">
                        <span className="tooltip-title">Multi Trip</span>
                        Ideal for the frequent traveler -- travel insurance for
                        unlimited number of trips per year, 90 days per trip.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
              {/* Plan Type - aligned next to "Choose type of trip" for Single Trip */}
              {formData.tripType === "Single Trip" && (
                <div
                  className={`travel-information-form-group ${getFieldErrorClass("planType")}`}
                >
                  <label className="form-label">Plan *</label>
                  <div className="radio-group">
                    {planTypeOptions.map((option) => (
                      <label className="radio-option" key={option}>
                        <input
                          type="radio"
                          name="planType"
                          value={option}
                          checked={formData.planType === option}
                          onChange={(e) => {
                            setPlanType(e.target.value as PlanType);
                            handleInputChange(
                              "planType",
                              e.target.value as TravelFormData["planType"],
                            );
                          }}
                        />
                        <span className="radio-custom"></span>
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* {shouldShowTripCategory && tripCategory === 'Inbound' && (
            <div className={`travel-information-form-group ${getFieldErrorClass('homeCountry')}`}>
              <label className="form-label">Home Country</label>
              <SearchableDropdown
                options={inboundlocationOptions}
                selectedValue={formData.homeCountry || ''}
                onChange={(value) => handleInputChange('homeCountry', value)}
                placeholder={inboundLoading ? "Loading countries..." : "Choose your home country"}
                searchPlaceholder="Search countries..."
                disabled={inboundLoading}
                dropdownId="home-country-dropdown"
              />
            </div>
          )} */}

          {/* Conditionally render fields based on trip type */}
          {formData.tripType === "Single Trip" && (
            <>
              {/* Plan Type - only rendered here when not already shown alongside "Select your mode of travel" or "Choose type of trip" */}
              {!shouldShowTypeOfTrip() && !shouldShowTransportMode() && (
                <div className="travel-plan-purpose-form-row">
                  <div
                    className={`travel-information-form-group grid-cols-2 ${getFieldErrorClass("planType")}`}
                  >
                    <label className="form-label">Plan *</label>
                    <div className="radio-group">
                      {planTypeOptions.map((option) => (
                        <label className="radio-option" key={option}>
                          <input
                            type="radio"
                            name="planType"
                            value={option}
                            checked={formData.planType === option}
                            onChange={(e) => {
                              setPlanType(e.target.value as PlanType);
                              handleInputChange(
                                "planType",
                                e.target.value as TravelFormData["planType"],
                              );
                            }}
                          />
                          <span className="radio-custom"></span>
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Purpose of Travel */}
              <div className="travel-plan-purpose-form-row">
                <div
                  className={`travel-information-form-group purpose-of-travel-full-width ${getFieldErrorClass("purposeOfTravel")}`}
                >
                  <label className="form-label">Purpose of Travel *</label>
                  <select
                    className={`form-select ${getFieldErrorClass("purposeOfTravel")}`}
                    value={formData.purposeOfTravel}
                    onChange={(e) =>
                      handleInputChange("purposeOfTravel", e.target.value)
                    }
                  >
                    <option value="">Select your purpose of travel</option>
                    {purposeOfTravelOptions.map((option) => (
                      <option key={option} value={option}>
                        {getPurposeOfTravelLabel(option)}
                      </option>
                    ))}
                  </select>
                  <p className="purpose-of-travel-helper-text">
                    Excludes travel for work
                    {tripCategory !== "Domestic" &&  " e.g., seasonal worker or OFWs"} 
                  </p>
                </div>
              </div>
              {/* Philippines Endpoint Question - Only for WorldwideElite and Asia and Oceania */}
              {(shouldShowPhilippinesQuestion() || tripCategory === "Inbound") && (
                <div
                  className={`travel-information-form-group ${getFieldErrorClass("philippinesEndpoint")}`}
                >
                  <label className="form-label">
                    Are you returning to {tripCategory === "Inbound" 
                      ? 'your country of origin' : 'the Philippines'}? *
                  </label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="philippinesEndpoint"
                        value="Yes"
                        checked={formData.philippinesEndpoint === "Yes"}
                        onChange={(e) =>
                          handleInputChange(
                            "philippinesEndpoint",
                            e.target.value as "Yes" | "No",
                          )
                        }
                      />
                      <span className="radio-custom"></span>
                      Yes
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="philippinesEndpoint"
                        value="No"
                        checked={formData.philippinesEndpoint === "No"}
                        onChange={(e) =>
                          handleInputChange(
                            "philippinesEndpoint",
                            e.target.value as "Yes" | "No",
                          )
                        }
                      />
                      <span className="radio-custom"></span>
                      No
                    </label>
                  </div>
                </div>
              )}

              {/* Location Fields Row */}
              <div
                className={`travel-information-form-row ${tripCategory === "Inbound" 
                  ? (formData.philippinesEndpoint === "Yes" ? "!grid-cols-3" : "!grid-cols-2") 
                  : ""}`}
              >
                <div
                  className={`form-group ${getFieldErrorClass("startTrip")}`}
                >
                  <label className="form-label">
                    {tripCategory === "Inbound"
                      ? "Country of Origin *"
                      : "Trip begins at *"}
                  </label>
                  <SearchableDropdown
                    options={
                      tripCategory === "Inbound"
                        ? internationalDestinationOptions
                        : startAndEndlocationOptions
                    }
                    selectedValue={formData.startTrip}
                    onChange={(value) => handleInputChange("startTrip", value)}
                    placeholder={
                      tripCategory === "Inbound"
                        ? "Country of Origin"
                        : provincesLoading
                          ? "Loading provinces..."
                          : "Choose your start trip"
                    }
                    searchPlaceholder={
                      tripCategory === "Inbound"
                        ? "Search countries..."
                        : "Search provinces..."
                    }
                    disabled={provincesLoading}
                  />
                </div>

                <div
                  className={`form-group ${getFieldErrorClass("destination")}`}
                >
                  <label className="form-label">
                    Destination/s{`${tripCategory === "Inbound" ? "" : " *"}`}
                  </label>
                  <MultiSelectDropdown
                    options={getDestinationOptions()}
                    selectedValues={formData.destination}
                    onChange={handleDestinationChange}
                    placeholder={
                      getDestinationLoading()
                        ? "Loading destinations..."
                        : "Select your destination"
                    }
                    searchPlaceholder="Search destinations..."
                    disabled={
                      getDestinationLoading() || tripCategory === "Inbound"
                    }
                    maxSelections={13}
                  />
                </div>
                {(tripCategory !== "Inbound" || formData.philippinesEndpoint === "Yes") &&
                  <div
                    className={`form-group ${getFieldErrorClass("endTrip")}`}
                  >
                    <label className="form-label">
                      {tripCategory === "Inbound"
                        ? "Return country"
                        : "Trip ends at *"
                      }
                    </label>
                    <SearchableDropdown
                      options={
                        formData.philippinesEndpoint === "No"
                          ? internationalDestinationOptions
                          : startAndEndlocationOptions
                      }
                      selectedValue={
                        tripCategory === ("Inbound" as TripCategory)
                          ? formData.startTrip
                          : formData.endTrip
                      }
                      onChange={(value) => handleInputChange("endTrip", value)}
                      placeholder={getEndTripPlaceholder()}
                      searchPlaceholder={
                        shouldShowPhilippinesQuestion() &&
                        (formData.philippinesEndpoint === "No" ||
                          tripCategory === ("Inbound" as TripCategory))
                          ? "Search countries..."
                          : "Search provinces..."
                      }
                      disabled={
                        tripCategory === ("Inbound" as TripCategory) ||
                        getEndTripLoading()
                      }
                    />
                  </div>
                }
              </div>
              {/* Travel Dates */}
              <div
                className={`travel-information-form-group ${getFieldErrorClass("startDate")} ${getFieldErrorClass("endDate")}`}
              >
                <label className="form-label travel-dates-label">
                  Travel Dates *
                </label>
                <div className="date-row">
                  <div
                    className={`date-group ${getFieldErrorClass("startDate")}`}
                  >
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(date) => handleInputChange("startDate", date)}
                      placeholder="MM/DD/YYYY"
                      validationRules={startDateRules}
                      name="startDate"
                    />
                  </div>
                  <div
                    className={`date-group ${getFieldErrorClass("endDate")}`}
                  >
                    <DatePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={(date) => handleInputChange("endDate", date)}
                      placeholder="MM/DD/YYYY"
                      validationRules={endDateRules}
                      name="endDate"
                      disabled={!formData.startDate}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Annual Multi-Trip Fields */}
          {formData.tripType === "Annual Multi-Trip" && (
            <div className="travel-information-form-row annual-multi-trip-row">
              <div
                className={`form-group ${getFieldErrorClass("purposeOfTravel")}`}
              >
                <label className="form-label">Purpose of Travel *</label>
                <select
                  className={`form-select ${getFieldErrorClass("purposeOfTravel")}`}
                  value={formData.purposeOfTravel}
                  onChange={(e) =>
                    handleInputChange("purposeOfTravel", e.target.value)
                  }
                >
                  <option value="">Select your purpose of travel</option>
                  {purposeOfTravelOptions.map((option) => (
                    <option key={option} value={option}>
                      {getPurposeOfTravelLabel(option)}
                    </option>
                  ))}
                </select>
                <p className="purpose-of-travel-helper-text">
                  Excludes travel for work e.g., seasonal worker or OFWs
                </p>
              </div>
              <div className={`form-group ${getFieldErrorClass("travelDate")}`}>
                <label className="form-label">Travel Date *</label>
                <DatePicker
                  label=""
                  value={formData.travelDate}
                  onChange={(date) => handleInputChange("travelDate", date)}
                  placeholder="MM/DD/YYYY"
                  validationRules={travelDateRules}
                  name="travelDate"
                  position="above"
                />
              </div>
            </div>
          )}

          {/* Form Actions - Show Plans and Buy Now Buttons */}
          <div className="travel-information-form-actions">
            {/* BUY NOW Button - Only show when onBuyNow callback is provided */}
            {onBuyNow && (
              <button
                type="button"
                className="buy-now-button"
                onClick={handleBuyNow}
              >
                BUY NOW
              </button>
            )}

            {/* SEE MY QUOTE Button (formerly SHOW PLANS) - Always enabled */}
            <button
              type="button"
              className="show-plans-button"
              onClick={onSeeMyQuote ? handleSeeMyQuote : handleShowPlans}
            >
              {onSeeMyQuote
                ? "SEE MY QUOTE"
                : onBuyNow
                  ? "SEE MY QUOTE"
                  : "SHOW PLANS"}
            </button>
          </div>
        </form>
      </div>

      <TravelInsuranceReminderModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        section={planSelection?.section}
      />

      <GroupPlanModal
        isOpen={isGroupModalOpen}
        onClose={handleCloseGroupModal}
      />

      <FamilyPlanModal
        isOpen={isFamilyModalOpen}
        onClose={handleCloseFamilyModal}
      />

      {/* Trip Category Info Modal */}
      <TripCategoryReminderModal
        isOpen={isTripCategoryInfoModalOpen}
        onClose={handleCloseTripCategoryInfoModal}
        tripCategory={tripCategory}
        transportMode={formData.transportMode}
      />
    </div>
  );
};

export default TravelInformationSection;
